import {
  Box,
  Divider,
  FormControl,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  HCWMessageData,
  MessageType,
  MessageVisibility,
  PatientDetailData,
  Role,
  useGetHCWMessagesQuery,
  useLazyPollHCWMessagesQuery,
  useSendHCWMessageMutation,
  useSendHCWTemplateMutation,
  useSetHCWMessageVisibilityMutation,
  useSetPendingHCWMessageStateMutation,
} from "../features/data/patientApiSlice";
import {
  addErrorDescription,
  selectIssues,
} from "../features/appState/issuesSlice";
import { combineSx, createStyleSheet, globalStyles } from "../utils/styles";
import { store, useAppDispatch } from "../features/store";
import {
  templateMessages,
  templateTitleOverrides,
} from "../utils/nudgeTemplateData";

import ChatMenuIcon from "@mui/icons-material/MoreVert";
import ChatMessageEntry from "./ChatMessageEntry";
import ChatScrollContainer from "./ChatScrollContainer";
import EmptyPage from "../../assets/chatux_no_messages.png";
import IconButton from "./IconButton";
import React from "react";
import SendMessageIcon from "@mui/icons-material/Send";
import TextInput from "./TextInput";
import { formatDistance } from "date-fns";
import { getErrorText } from "../utils/errors";
import { logEvent } from "../features/api/apiSlice";
import { setUnsavedWork } from "../features/appState/uxSlice";
import { theme } from "../../theme";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getPatientGroup } from "../features/auth/authSlice";
import { getTemplateInterpolation } from "../utils/strings";

const NUDGE_EXPIRATION_MS = 23 * 60 * 60 * 1000; // 23 hours
const MESSAGE_PERSIST_TIME = 1 * 60 * 1000; // 1 minute
type HCWMessageDataWithRequestId = HCWMessageData & { requestId: string };

const ChatUx = (props: {
  chatOpen: boolean;
  patient: PatientDetailData;
  updateNudgeData?: (
    numNudgesSinceLastClientMessage: number,
    minNudgeMessageAge: number
  ) => void;
}) => {
  const { chatOpen, patient, updateNudgeData } = props;
  const { t } = useTranslation("ChatUx");
  const dispatch = useAppDispatch();
  const issues = useSelector(selectIssues);
  const patientGroup = getPatientGroup(store.getState().auth);

  // Component state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [messagesToRender, setMessagesToRender] = useState<HCWMessageData[]>(
    []
  );
  const [mostRecentClientMessageAge, setMostRecentClientMessageAge] =
    useState(0);
  const [mostRecentNudgeMessageAge, setMostRecentNudgeMessageAge] = useState(0);
  const [nudgeTemplate, setNudgeTemplate] = useState("");
  const [pollingCursor, setPollingCursor] = useState<string | null>(null);
  const [pendingMessages, setPendingMessages] = useState<
    HCWMessageDataWithRequestId[]
  >([]);
  const [messageInput, setMessageInput] = useState("");
  const hasUnsavedMessage = messageInput.trim() !== "";

  // Queries/mutations
  const {
    data: chatData,
    isError,
    error,
  } = useGetHCWMessagesQuery({ uid: patient.uid, cursor: null });
  const [pollHCWMessages, pollHCWMessagesResult] = useLazyPollHCWMessagesQuery({
    pollingInterval: 15000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [sendHCWMessage, { isLoading: isSendingMessage }] =
    useSendHCWMessageMutation();
  const [sendHCWTemplate, { isLoading: isSendingNudgeTemplate }] =
    useSendHCWTemplateMutation();
  const [setPendingHCWMessageState] = useSetPendingHCWMessageStateMutation();
  const [setHCWMessageVisibility] = useSetHCWMessageVisibilityMutation();

  const isSendingMessageRef = useRef(false);
  const isSendingNudgeRef = useRef(false);

  // Callbacks
  const handleSendMessageClick = useCallback(() => {
    const messageText = messageInput.trim();
    if (messageText.length > 0 && !isSendingMessageRef.current) {
      isSendingMessageRef.current = true;
      const sendResult = sendHCWMessage({
        uid: patient.uid,
        text: messageText,
      });
      sendResult.finally(() => {
        isSendingMessageRef.current = false;
      });
      const requestId = sendResult.requestId;
      dispatch(
        addErrorDescription({
          requestId,
          errorDescription: t("messageSendError", {
            name: `${patient.name} (${patient.referral_id})`,
          }),
        })
      );
      setPendingMessages((prev) => [
        ...(prev || []),
        {
          message_id: `pending-${new Date().toISOString()}`,
          role: Role.hcw,
          type: MessageType.text,
          text: messageText,
          visibility: MessageVisibility.visible,
          attachment: null,
          sent_ts: new Date().toISOString(),
          hcw: {
            name: store.getState().auth.name,
            picture: store.getState().auth.picture,
          },
          in_reply_to: null,
          reactions: [],
          delivery_status: null,
          requestId,
        },
      ]);
      logEvent(dispatch, {
        event: "clinicianChatResponse",
        data: { patientUid: patient.uid, messageType: MessageType.text },
      });
      setMessageInput("");
    }
  }, [
    messageInput,
    setMessageInput,
    patient.uid,
    t,
    patient.name,
    patient.referral_id,
    sendHCWMessage,
    dispatch,
  ]);
  const handleMessageInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setMessageInput(event.target.value);
    },
    [setMessageInput]
  );
  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter") {
        handleSendMessageClick();
        event.preventDefault();
      }
    },
    [handleSendMessageClick]
  );
  const handleNudgeTemplateChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      setNudgeTemplate(event.target.value);
    },
    []
  );
  const handleNudgeTemplateClick = useCallback(() => {
    if (nudgeTemplate && !isSendingNudgeRef.current) {
      isSendingNudgeRef.current = true;
      const sendResult = sendHCWTemplate({
        uid: patient.uid,
        templateName: nudgeTemplate,
      });
      sendResult.finally(() => {
        isSendingNudgeRef.current = false;
      });
      const requestId = sendResult.requestId;
      dispatch(
        addErrorDescription({
          requestId,
          errorDescription: t("messageSendError", {
            name: `${patient.name} (${patient.referral_id})`,
          }),
        })
      );
      setPendingMessages((prev) => [
        ...(prev || []),
        {
          message_id: `pending-${nudgeTemplate}`,
          role: Role.hcw,
          type: MessageType.template,
          text: t("sentTemplateMessage") + nudgeTemplate,
          visibility: MessageVisibility.visible,
          attachment: null,
          sent_ts: new Date().toISOString(),
          hcw: {
            name: store.getState().auth.name,
            picture: store.getState().auth.picture,
          },
          in_reply_to: null,
          reactions: [],
          delivery_status: null,
          requestId,
        },
      ]);
      logEvent(dispatch, {
        event: "clinicianChatResponse",
        data: { patientUid: patient.uid, messageType: MessageType.template },
      });
    }
  }, [
    nudgeTemplate,
    patient.uid,
    t,
    patient.name,
    patient.referral_id,
    sendHCWTemplate,
    dispatch,
  ]);
  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
    e.stopPropagation();
  }, []);
  const handleMenuClose = useCallback((e: React.SyntheticEvent) => {
    setMenuAnchor(null);
    e.stopPropagation();
  }, []);
  const handleMarkAsReadToggle = useCallback(() => {
    setMenuAnchor(null);
    setPendingHCWMessageState({
      uid: patient.uid,
      pendingHCWMessage: !patient.pending_hcw_message,
    });
    logEvent(dispatch, {
      event: !patient.pending_hcw_message
        ? "markConversationAsRead"
        : "markConversationAsUnread",
      data: { patientUid: patient.uid },
    });
  }, [
    setPendingHCWMessageState,
    patient.uid,
    patient.pending_hcw_message,
    dispatch,
  ]);
  const handleMessageVisibilityChange = useCallback(
    (messageUid: string, visibility: MessageVisibility) => {
      setHCWMessageVisibility({
        patientUid: patient.uid,
        messageUid,
        visibility,
      });
      logEvent(dispatch, {
        event: "messageVisibilityChanged",
        data: {
          patientUid: patient.uid,
          messageUid,
          visibility,
        },
      });
    },
    [setHCWMessageVisibility, patient.uid, dispatch]
  );

  // Reactive code
  useEffect(() => {
    if (!hasUnsavedMessage) {
      return;
    }
    dispatch(setUnsavedWork({ key: "chat", isDirty: true }));
    return () => {
      dispatch(setUnsavedWork({ key: "chat", isDirty: false }));
    };
  }, [dispatch, hasUnsavedMessage]);
  useEffect(() => {
    // When the polling cursor is retrieved, set it in the component state
    const newPollingCursor = chatData?.pollingCursor || null;
    setPollingCursor(newPollingCursor);
  }, [chatData?.pollingCursor]);
  useEffect(() => {
    const newPollingCursor = pollHCWMessagesResult.data?.pollingCursor || null;
    setPollingCursor(newPollingCursor);
  }, [pollHCWMessagesResult.data?.pollingCursor]);
  useEffect(() => {
    // Poll for new messages
    if (pollingCursor) {
      pollHCWMessages({
        uid: patient.uid,
        pollingCursor,
        timeoutSeconds: 15,
      });
    }
  }, [pollingCursor, patient.uid, pollHCWMessages]);
  useEffect(() => {
    // Compute the messages to render
    const updatedMessagesToRender = [...(chatData?.messages || [])];
    // It's possible that polling has returned a message(s) that are already
    // in chatData (i.e. they share the same message_id), which means we should
    // update them. This can happen if e.g. a user adds a reaction to a
    // message.
    pollHCWMessagesResult?.data?.messages.forEach((message) => {
      const i = updatedMessagesToRender.findIndex(
        (m) => m.message_id === message.message_id
      );
      if (i >= 0) {
        // Message exists with the same message_id, update it
        updatedMessagesToRender[i] = message;
      } else {
        updatedMessagesToRender.push(message);
      }
    });
    messagesToRender.forEach((message) => {
      if (
        !updatedMessagesToRender.find(
          (m) => m.message_id === message.message_id
        )
      ) {
        updatedMessagesToRender.push(message);
      }
    });
    updatedMessagesToRender.sort((a, b) => {
      const aTime = new Date(a.sent_ts).getTime();
      const bTime = new Date(b.sent_ts).getTime();
      if (aTime > bTime) {
        return 1;
      } else if (aTime < bTime) {
        return -1;
      } else {
        return 0;
      }
    });
    setMessagesToRender(updatedMessagesToRender);

    // Remove any pending messages that now show up in the updated list.
    // A pending message is considered to be in the updated messages list
    // if there is a message with the same role, type, and text, and was
    // sent within the MESSAGE_PERSIST_TIME window.
    const updatedPendingMessages: HCWMessageDataWithRequestId[] = [];
    pendingMessages.forEach((pendingMessage) => {
      if (
        !updatedMessagesToRender.find((message) => {
          // If the pending message was sent less than a little bit before
          // or after an otherwise matching message, consider it a match.
          // If it was sent more than a little bit before the otherwise matching
          // message, it's not a match since pending messages always exist before
          // the real message.
          const timeDiff = Math.abs(
            new Date(message.sent_ts).getTime() -
              new Date(pendingMessage.sent_ts).getTime()
          );
          return (
            message.role === pendingMessage.role &&
            message.type === pendingMessage.type &&
            message.text === pendingMessage.text &&
            timeDiff < MESSAGE_PERSIST_TIME
          );
        })
      ) {
        updatedPendingMessages.push(pendingMessage);
      }
    });
    if (updatedPendingMessages.length !== pendingMessages.length) {
      setPendingMessages(updatedPendingMessages);
    }

    // Compute whether nudge UX should be shown
    const timeNow = Date.now();
    const minClientMessageAge = Math.min(
      ...updatedMessagesToRender
        .filter((m) => m.role === Role.user)
        .map((m) => timeNow - new Date(m.sent_ts).getTime())
    );
    setMostRecentClientMessageAge(minClientMessageAge);

    const minNudgeMessageAge = Math.min(
      ...[...updatedMessagesToRender, ...updatedPendingMessages]
        .filter((m) => m.role === Role.hcw && m.type === MessageType.template)
        .map((m) => timeNow - new Date(m.sent_ts).getTime())
    );
    setMostRecentNudgeMessageAge(minNudgeMessageAge);

    const numNudgesSinceLastClientMessage = updatedMessagesToRender.filter(
      (m) =>
        m.role === Role.hcw &&
        m.type === MessageType.template &&
        new Date(m.sent_ts).getTime() > timeNow - minClientMessageAge
    ).length;

    updateNudgeData?.(numNudgesSinceLastClientMessage, minNudgeMessageAge);

    // Disable warning about messagesToRender needing to be included in the dependency array
  }, [
    chatData?.messages,
    pollHCWMessagesResult?.data?.messages,
    pendingMessages,
    updateNudgeData,
  ]);
  useEffect(() => {
    // Remove any pending messages that are now in the issues list
    setPendingMessages((prev) =>
      prev.filter(
        (message) => !issues.some((issue) => issue.id === message.requestId)
      )
    );
  }, [issues]);

  const renderMenuItem = (template: string, value?: string) => (
    <MenuItem
      key={template}
      value={typeof value !== "undefined" ? value : template}
      sx={{ whiteSpace: "normal" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="detailCellBody">
          {t("ChatUx:templateTitles:" + template)}
        </Typography>
      </Box>
    </MenuItem>
  );

  const messageList = [...messagesToRender, ...pendingMessages];
  return (
    <Box
      sx={{
        ...styles.paneContainer,
        opacity: chatOpen ? 1 : 0,
        pointerEvents: chatOpen ? "auto" : "none",
      }}
    >
      <Box sx={styles.headerContainer}>
        <Box sx={styles.headerTextContainer}>
          <Typography variant="detailHeaderTitle" noWrap>
            {t("chat", { name: patient.name })}
          </Typography>
          <Typography variant="detailCellSubtitle">
            {t("conversationSummaryDetailWarning")}
          </Typography>
        </Box>
        <IconButton
          disabled={messageList.length === 0}
          icon={<ChatMenuIcon />}
          tooltip={t("common:menu")}
          onClick={handleMenuClick}
          size="small"
        />
        <Menu
          anchorEl={menuAnchor}
          open={menuAnchor != null}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            list: {
              role: "listbox",
            },
          }}
        >
          <MenuItem onClick={handleMarkAsReadToggle}>
            {t(patient.pending_hcw_message ? "markAsRead" : "markAsUnread")}
          </MenuItem>
        </Menu>
      </Box>
      <Divider sx={styles.topDivider} />
      {messageList.length > 0 ? (
        <ChatScrollContainer>
          {messageList.map((message) => (
            <ChatMessageEntry
              key={message.message_id}
              message={message}
              patientName={patient.name}
              patientGroup={patientGroup}
              onMessageVisibilityChange={handleMessageVisibilityChange}
              chatOpen={chatOpen}
            />
          ))}
        </ChatScrollContainer>
      ) : (
        <Box sx={styles.noMessagesContainer}>
          {isError ? (
            <Typography variant="errorBody">{getErrorText(error)}</Typography>
          ) : (
            <>
              <Box sx={styles.noMessagesImage} />
              <Typography
                variant="body2"
                sx={combineSx(globalStyles.gapTop, styles.noMessagesText)}
              >
                {t("noMessages")}
              </Typography>
              <Typography variant="body2" sx={styles.noMessagesText}>
                {t("noMessages2")}
              </Typography>
            </>
          )}
        </Box>
      )}
      <Divider sx={styles.bottomDivider} />
      {mostRecentClientMessageAge > NUDGE_EXPIRATION_MS ? (
        <>
          <FormControl>
            <Select
              size="small"
              disabled={
                mostRecentNudgeMessageAge < NUDGE_EXPIRATION_MS ||
                isSendingNudgeTemplate
              }
              displayEmpty
              value={nudgeTemplate}
              onChange={handleNudgeTemplateChange}
              sx={{ ...globalStyles.gapTop, backgroundColor: "white" }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                sx: { width: 200 },
              }}
            >
              {renderMenuItem("none", "")}
              {(
                templateMessages[patient.preferred_language] ||
                templateMessages["en"]
              ).map((template) =>
                renderMenuItem(
                  templateTitleOverrides[template.template] ||
                    template.template,
                  template.template
                )
              )}
            </Select>
          </FormControl>
          <Box sx={styles.nudgeTemplateContainer}>
            <Typography
              variant={nudgeTemplate ? "detailCellBody" : "detailCellSubtitle"}
              sx={combineSx(
                combineSx(styles.nudgeTemplateText, styles.nudgeTemplateTextNormal),
                { color: nudgeTemplate ? "inherit" : "GrayText" }
              )}
              dangerouslySetInnerHTML={{
                __html: t(
                  "ChatUx:templates:" + (nudgeTemplate || "none"),
                  getTemplateInterpolation(patientGroup, nudgeTemplate)
                ),
              }}
            />
            <IconButton
              disabled={
                !nudgeTemplate ||
                mostRecentNudgeMessageAge < NUDGE_EXPIRATION_MS ||
                isSendingNudgeTemplate
              }
              icon={
                <SendMessageIcon
                  fontSize="small"
                  color={
                    !nudgeTemplate ||
                    mostRecentNudgeMessageAge < NUDGE_EXPIRATION_MS ||
                    isSendingNudgeTemplate
                      ? "disabled"
                      : "primary"
                  }
                />
              }
              tooltip={t("nudge")}
              onClick={handleNudgeTemplateClick}
            />
          </Box>
        </>
      ) : (
        <>
          <TextInput
            ariaLabel={"chat"}
            sx={styles.messageInput}
            inputProps={{
              placeholder: t("typeMessage"),
              onKeyDown: handleOnKeyDown,
              value: messageInput,
              onChange: handleMessageInputChange,
              multiline: true,
              endAdornment: (
                <IconButton
                  disabled={messageInput.length === 0 || isSendingMessage}
                  icon={
                    <SendMessageIcon
                      fontSize="small"
                      color={
                        messageInput.length > 0 && !isSendingMessage
                          ? "primary"
                          : "disabled"
                      }
                    />
                  }
                  tooltip={t("common:send")}
                  onClick={handleSendMessageClick}
                  sx={{ marginRight: 1 }}
                />
              ),
            }}
          />
          <Typography sx={styles.chatExpiryText} variant="detailCellSubtitle">
            {t("chatExpiry", {
              time: formatDistance(
                0,
                NUDGE_EXPIRATION_MS - mostRecentClientMessageAge
              ),
            })}
          </Typography>
        </>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  paneContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    transition: (theme) =>
      theme.transitions.create("opacity", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
    backgroundColor: (theme) => theme.palette.grey[100],
    p: 3,
    overflowY: "hidden",
    minWidth: (theme) => theme.spacing(56),
    [theme.breakpoints.down("desktop")]: {
      minWidth: theme.spacing(40),
    },
  },
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  headerTextContainer: {
    width: "calc(100% - 32px)",
    display: "flex",
    flexDirection: "column",
  },
  topDivider: {
    ...globalStyles.gapTop,
    mx: -3,
    [theme.breakpoints.down("desktop")]: {
      mx: -2,
    },
  },
  bottomDivider: {
    mx: -3,
    [theme.breakpoints.down("desktop")]: {
      mx: -2,
    },
  },
  noMessagesContainer: {
    p: 1,
    mx: -3,
    [theme.breakpoints.down("desktop")]: {
      mx: -2,
    },
    backgroundColor: "white",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  noMessagesImage: {
    flex: 1,
    backgroundImage: `url(${EmptyPage})`,
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: "100%",
    minHeight: "100px",
    maxWidth: "274px",
    maxHeight: "277px",
  },
  nudgeTemplateContainer: {
    ...globalStyles.gapTop,
    display: "flex",
    flexDirection: "row",
    backgroundColor: "white",
    borderRadius: 5,
    alignItems: "center",
    padding: (theme) => theme.spacing(0, 1, 0, 2),
  },
  nudgeTemplateText: {
    flex: 1,
    my: 1,
    marginRight: 0.5,
  },
  nudgeTemplateTextNormal: {
    fontWeight: "normal",
  },
  chatExpiryText: {
    textAlign: "right",
  },
  noMessagesText: {
    color: "GrayText",
  },
  messageInput: {
    ...globalStyles.gapTop,
    marginBottom: 1,
    padding: (theme) => theme.spacing(0, 0, 0, 2),
  },
  nudgeButton: {
    alignSelf: "end",
    minWidth: "80px",
    backgroundColor: "white",
  },
});

export default ChatUx;
